import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { Marker } from "@react-google-maps/api";
import {
  GoogleMap,
  GoogleMapProps,
  ZoomControl,
  ZoomControlText,
  ZoomControlWrapper,
} from "../Map";
import { Typography } from "../Typography";

export type ProjectMapLocation = {
  latitude: number;
  longitude: number;
  locationName: string;
  project: {
    title: string;
    description: string;
    url: string;
  };
};

export type ProjectLocationsMapProps = {
  locations: ProjectMapLocation[];
};

const ProjectLocationMapWrapper = styled.div`
  background-color: ${(props) => props.theme.colors.snowdrop};
  display: flex;
  flex-flow: column;
  @media ${(props) => props.theme.bp.tabletUp} {
    flex-flow: row;
    align-items: stretch;
  }
`;

// GoogleMap has a width of 100% always, so add flex-shrink: 0 to ensure
// that the map shrinks & not this element - always stays at-least 33.3% wide
const ProjectWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-flow: column;
  padding: 24px;
  width: 100%;
  @media ${(props) => props.theme.bp.tabletUp} {
    width: 33.3%;
  }
`;

const LocationWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 27px;
`;

const LocationTitle = styled(Typography)`
  margin-left: 20px;
  color: ${(props) => props.theme.colors.charcoal};
`;

const ProjectTitle = styled(Typography)`
  margin-bottom: 8px;
`;

const ProjectDescription = styled(Typography)`
  margin-bottom: 32px;
`;

const ProjectLink = styled(Typography)``;

const MapContainer = styled.div`
  height: 194px;
  width: 100%;

  @media ${(props) => props.theme.bp.tabletUp} {
    height: 464px;
  }
`;

export const ProjectLocationsMap = (props: ProjectLocationsMapProps) => {
  const [mapOptions, setMapOptions] =
    useState<GoogleMapProps["initialOptions"]>();

  const [selectedLocation, setSelectedLocation] = useState<ProjectMapLocation>(
    props.locations[0]
  );

  const mapElement = useRef<google.maps.Map>();
  const mapContainerStyle = {
    width: "100%",
    height: "100%",
  };

  // Set options on component mount & only render once initial options are ready
  // map does not re-render on options props change
  useEffect(() => {
    const options: GoogleMapProps["initialOptions"] = {
      disableDefaultUI: true,
      zoomControl: false,
    };

    if (selectedLocation)
      options.center = {
        lat: selectedLocation.latitude,
        lng: selectedLocation.longitude,
      };

    setMapOptions(options);
  }, []);

  const zoomIn = () =>
    mapElement.current?.setZoom(mapElement.current!.getZoom()! + 1);

  const zoomOut = () =>
    mapElement.current?.setZoom(mapElement.current!.getZoom()! - 1);

  return (
    <ProjectLocationMapWrapper>
      {mapOptions && (
        <MapContainer>
          <GoogleMap
            onLoad={(map) => (mapElement.current = map)}
            initialOptions={mapOptions}
            containerStyle={mapContainerStyle}
          >
            {props.locations.map((location, idx) => (
              <Marker
                key={`marker-${idx}`}
                icon={{
                  url:
                    selectedLocation === location
                      ? "/images/app/map/marker-red.svg"
                      : "/images/app/map/marker-black.svg",
                }}
                position={{ lat: location.latitude, lng: location.longitude }}
                onClick={() => setSelectedLocation(location)}
              />
            ))}

            <ZoomControlWrapper>
              <ZoomControl onClick={zoomIn} style={{ marginBottom: "4px" }}>
                <ZoomControlText>+</ZoomControlText>
              </ZoomControl>
              <ZoomControl onClick={zoomOut}>
                <ZoomControlText>-</ZoomControlText>
              </ZoomControl>
            </ZoomControlWrapper>
          </GoogleMap>
        </MapContainer>
      )}

      <ProjectWrapper>
        {selectedLocation ? (
          <>
            <LocationWrapper>
              <img src={`/images/app/map/marker-red.svg`}></img>

              <LocationTitle use="body1" as="p">
                {selectedLocation.locationName}
              </LocationTitle>
            </LocationWrapper>

            <ProjectTitle use="sectionTitle" as="h5">
              {selectedLocation.project.title}
            </ProjectTitle>

            <ProjectDescription use="body1" as="p">
              {selectedLocation.project.description}
            </ProjectDescription>

            <ProjectLink
              use="standaloneLink"
              as="a"
              target="_blank"
              href={selectedLocation.project.url}
              rel="noopener noreferrer"
            >
              Visit project page
            </ProjectLink>
          </>
        ) : (
          <h1>No location selected</h1>
        )}
      </ProjectWrapper>
    </ProjectLocationMapWrapper>
  );
};
